import { accessToken } from '../auth';
import { config } from '../env';

type ReportName = 'arrears' | 'weekly';

/** Thrown when the API refuses the report (e.g. 403 for a reader). */
export class ReportError extends Error {
  constructor(readonly status: number) {
    super(`HTTP ${status}`);
  }
}

async function fetchReport(
  name: ReportName,
  query: Record<string, string>,
): Promise<Blob> {
  const url = new URL(`/reports/${name}.pdf`, config.graphqlUrl);
  for (const [key, value] of Object.entries(query)) {
    url.searchParams.set(key, value);
  }
  const token = await accessToken();
  const res = await fetch(url, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new ReportError(res.status);
  return res.blob();
}

/**
 * arqam's committee PDFs (`GET /reports/<name>.pdf`) need the login token,
 * so a plain link can't open them: fetch with the token, then show the file.
 * The tab is opened at the tap (browsers block a window opened later) and
 * filled when the PDF arrives.
 */
export async function openReportPdf(
  name: ReportName,
  query: Record<string, string> = {},
): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    const file = URL.createObjectURL(await fetchReport(name, query));
    if (tab) tab.location.href = file;
    else window.location.href = file;
  } catch (error) {
    tab?.close();
    throw error;
  }
}

/** The same PDF saved as a file (arqam's `?download=1`). */
export async function downloadReportPdf(
  name: ReportName,
  fileName: string,
  query: Record<string, string> = {},
): Promise<void> {
  const blob = await fetchReport(name, { ...query, download: '1' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
